import {
  getNotebookDocument,
  listNotebookDocuments,
} from "@/gen_src/proto/chalk/notebook/v1/document_service-NotebookDocumentService_connectquery";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
// eslint-disable-next-line no-restricted-imports
import { useQuery } from "@connectrpc/connect-query";
import { useQuery as useTanStackQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

export interface NotebookBreadcrumbSibling {
  id: string;
  title: string;
  description: string;
}

export interface NotebookBreadcrumbData {
  notebookId: string;
  /** Current notebook's title; null until the document resolves. */
  title: string | null;
  siblings: readonly NotebookBreadcrumbSibling[];
}

const NOTEBOOK_BREADCRUMB_SIBLINGS_LIMIT = 100;
const notebookBreadcrumbTitleDraftKey = (notebookId: string): QueryKey => [
  "notebook-breadcrumb-title-draft",
  notebookId,
];

/**
 * Publishes the editor's local title through a UI-only query cache entry observed by the top rail.
 * Keeping this separate from GetNotebookDocument preserves that query as the editor's authoritative
 * revision baseline while a save or refetch is in flight.
 */
export const useSetNotebookBreadcrumbTitle = (notebookId: string): ((title: string) => void) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => notebookBreadcrumbTitleDraftKey(notebookId), [notebookId]);

  useEffect(() => {
    // Draft titles are scoped to this editor mount. A later visit should start from the server
    // document rather than a value retained by the app-wide query client.
    queryClient.removeQueries({ queryKey, exact: true });
    return () => queryClient.removeQueries({ queryKey, exact: true });
  }, [queryClient, queryKey]);

  return useCallback(
    (title: string) => {
      queryClient.setQueryData(queryKey, title);
    },
    [queryClient, queryKey],
  );
};

/**
 * Title + sibling list for the notebook breadcrumb: the crumb shows the
 * notebook TITLE (raw id until it resolves, mirroring environment id→name
 * resolution) and a dropdown of the environment's notebooks with their
 * descriptions. Both queries are gated on actually being on a notebook page.
 */
export const useNotebookBreadcrumbData = (): NotebookBreadcrumbData | null => {
  const { notebookId } = useRouteInfo();
  const onNotebookPage = notebookId != null && notebookId.length > 0;
  const documentQuery = useQuery(
    getNotebookDocument,
    { notebookId: notebookId ?? "" },
    { enabled: onNotebookPage },
  );
  const notebooksQuery = useQuery(
    listNotebookDocuments,
    { cursor: "", limit: NOTEBOOK_BREADCRUMB_SIBLINGS_LIMIT },
    { enabled: onNotebookPage },
  );
  const titleDraftQuery = useTanStackQuery<string>({
    queryKey: notebookBreadcrumbTitleDraftKey(notebookId ?? ""),
    queryFn: () => Promise.resolve(""),
    enabled: false,
  });
  const title = documentQuery.data?.document?.title;
  const displayedTitle = titleDraftQuery.data ?? title;
  const documents = notebooksQuery.data?.documents;

  return useMemo(() => {
    if (notebookId == null || notebookId.length === 0) {
      return null;
    }
    return {
      notebookId,
      title: displayedTitle != null && displayedTitle.length > 0 ? displayedTitle : null,
      siblings: (documents ?? []).map((document) => ({
        id: document.id,
        title: document.title || "Untitled notebook",
        description: document.description ?? "",
      })),
    };
  }, [displayedTitle, documents, notebookId]);
};
